// The app's one voice controller and its store (BUILD 2 C2-8). The store is
// created at import and is inert; the controller when VoiceRoot mounts or a
// control first asks for it. Neither opens the microphone or a connection
// until voice mode is entered.
import { startCapture } from '@/speech/capture/core';
import { micOwner } from '@/speech/capture/mic-owner';
import { fetchSpeechConfig, mintSpeechSession } from '@/speech/transport/http';
import { guardCaptureStart } from '@/speech/policy-guard';
import { SpeechSocket } from '@/speech/transport/session-socket';
import { sharedAudioContext } from './audio-context';
import { putVoicePrefs } from './api';
import { voiceFocus } from './confirm/focus';
import type { VoicePrefs } from './contracts';
import { createVoiceController, type VoiceController } from './controller';
import { speechPlayer } from './playback/player';
import { liveAnswer } from './live-answer';
import { fullDuplexAvailable } from './full-duplex';
import { VoiceLink } from './session';
import { voiceConfigStore, voiceMode } from './state';
import { createVoiceStore } from './store';

export const voiceStore = createVoiceStore();

const timers = {
  setTimeout: (callback: () => void, ms: number) => globalThis.setTimeout(callback, ms),
  clearTimeout: (handle: unknown) => globalThis.clearTimeout(handle as number),
};
const now = () => (typeof performance !== 'undefined' ? performance.now() : Date.now());

type WakeLockSentinel = { release(): Promise<void> };
type WakeLockNavigator = { wakeLock?: { request(type: 'screen'): Promise<WakeLockSentinel> } };

/** Save a preference change, show it at once, then confirm it from the server. */
export async function saveVoicePrefs(patch: Partial<VoicePrefs>): Promise<void> {
  voiceConfigStore.adoptPrefs(await putVoicePrefs(patch));
  void voiceConfigStore.refresh();
}

let controller: VoiceController | null = null;

export function voiceController(): VoiceController {
  controller ??= createVoiceController({
    store: voiceStore,
    micOwner,
    startCapture: guardCaptureStart(fetchSpeechConfig, voiceConfigStore.scope, startCapture),
    createLink: (style, events, conversation) =>
      new VoiceLink(
        style,
        {
          mint: (request) => mintSpeechSession(request),
          openSocket: (options) => new SpeechSocket(options),
          timers,
          now,
          online: () => globalThis.navigator?.onLine !== false,
          endpointDelayMs: () => voiceConfigStore.current().voice?.prefs.endpointDelayMs ?? 1000,
          conversation,
        },
        events,
      ),
    focus: voiceFocus,
    player: speechPlayer,
    timers,
    now,
    visible: () => globalThis.document?.visibilityState !== 'hidden',
    online: () => globalThis.navigator?.onLine !== false,
    audioContext: () => {
      try {
        return sharedAudioContext();
      } catch {
        return null;
      }
    },
    config: () => voiceConfigStore.current(),
    fullDuplexAvailable: () => fullDuplexAvailable(voiceConfigStore.current().voice),
    setActive: (active) => voiceMode.set(active),
    liveAnswer: () => liveAnswer.current(),
    wakeLock: async () =>
      (await (globalThis.navigator as WakeLockNavigator | undefined)?.wakeLock?.request(
        'screen',
      )) ?? null,
    savePrefs: saveVoicePrefs,
  });
  return controller;
}
